import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Chip, Input, Spinner, Tab, Tabs, Textarea } from '@nextui-org/react';
import { apiTry } from '../lib/api';
import {
  PRESALE_EMAIL_FIELDS,
  PRESALE_EMAIL_LANG_NAMES,
  SAMPLE_CODE,
  renderPresaleCodeEmail
} from '../lib/presaleEmail';

// Agencias > Preventa > Correo del código: los textos del correo con el código de acceso, por
// idioma, con la vista previa al lado. El diseño es fijo; lo que se guarda es lo que envía la web.
// Worker: GET/PUT /api/backoffice/presale/email[/:lang] y POST …/:lang/test.

const fmt = (ms) => (ms ? new Date(ms).toLocaleString() : '');
const same = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

export default function PresaleEmailEditor({ canWrite }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});
  const [lang, setLang] = useState('es');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await apiTry('/api/backoffice/presale/email').catch((e) => ({ ok: false, data: { error: e.message } }));
    if (r.ok) {
      setLanguages(r.data.languages || []);
      setDefaults(r.data.defaults || {});
      setSaved(r.data.templates || {});
      setDrafts(Object.fromEntries((r.data.languages || []).map((l) => [l, { ...(r.data.defaults || {})[l], ...(r.data.templates?.[l]?.fields || {}) }])));
    } else {
      setError((r.data && r.data.error) || 'No se pudo cargar el correo');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const draft = drafts[lang] || {};
  const savedFields = saved[lang]?.fields || null;
  const dirty = !same(draft, savedFields || defaults[lang]) || !savedFields;
  const preview = useMemo(() => renderPresaleCodeEmail(draft, SAMPLE_CODE, lang), [draft, lang]);

  function setField(key, value) {
    setNotice(null);
    setDrafts((all) => ({ ...all, [lang]: { ...all[lang], [key]: value } }));
  }

  async function save() {
    setBusy('save');
    setError(null);
    setNotice(null);
    const r = await apiTry(`/api/backoffice/presale/email/${lang}`, { method: 'PUT', body: { fields: draft } }).catch((e) => ({ ok: false, data: { error: e.message } }));
    setBusy(null);
    if (!r.ok) {
      const label = PRESALE_EMAIL_FIELDS.find((f) => f.key === r.data?.field)?.label;
      setError(`${(r.data && r.data.error) || 'No se pudo guardar'}${label ? ` (${label})` : ''}`);
      return;
    }
    setSaved((all) => ({ ...all, [lang]: r.data.template }));
    setDrafts((all) => ({ ...all, [lang]: { ...r.data.template.fields } }));
    setNotice(`Guardado. A partir de ahora el correo en ${PRESALE_EMAIL_LANG_NAMES[lang]} sale así.`);
  }

  async function sendTest() {
    setBusy('test');
    setError(null);
    setNotice(null);
    const r = await apiTry(`/api/backoffice/presale/email/${lang}/test`, { method: 'POST', body: { fields: draft } }).catch((e) => ({ ok: false, data: { error: e.message } }));
    setBusy(null);
    if (!r.ok) { setError((r.data && r.data.error) || 'No se pudo enviar la prueba'); return; }
    setNotice(`Prueba enviada a ${r.data.to} (con lo que hay en pantalla, aunque no esté guardado).`);
  }

  if (loading) return <div className="flex justify-center p-8"><Spinner /></div>;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-default-500">
        El correo que recibe cada agencia con su código para entrar en agencias.tikcontrol.live. Edita los textos de cada idioma: la vista previa
        enseña exactamente lo que le llegará (con un código de ejemplo). El diseño, los colores y el recuadro del código son fijos.
      </p>

      <Tabs selectedKey={lang} onSelectionChange={(k) => { setLang(String(k)); setNotice(null); setError(null); }} aria-label="Idioma del correo">
        {languages.map((l) => {
          const changed = !same(drafts[l], saved[l]?.fields || null);
          return <Tab key={l} title={<span>{PRESALE_EMAIL_LANG_NAMES[l] || l}{changed ? ' •' : ''}</span>} />;
        })}
      </Tabs>

      {notice && <div className="rounded-md border border-success-200 bg-success-50 p-3 text-sm text-success-700">{notice}</div>}
      {error && <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_620px]">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2 text-xs text-default-500">
            {saved[lang] ? (
              <span>Última edición: {fmt(saved[lang].updatedAt)}{saved[lang].updatedBy ? ` · ${saved[lang].updatedBy}` : ''}</span>
            ) : (
              <Chip size="sm" color="warning" variant="flat">Sin guardar: ahora se envía el texto básico, sin diseño</Chip>
            )}
            {dirty && saved[lang] && <Chip size="sm" color="primary" variant="flat">Cambios sin guardar</Chip>}
          </div>
          {PRESALE_EMAIL_FIELDS.map((f) => {
            const value = draft[f.key] || '';
            const common = {
              label: f.label + (f.required ? ' *' : ''),
              value,
              onValueChange: (v) => setField(f.key, v),
              isReadOnly: !canWrite,
              maxLength: f.max,
              description: `${value.length}/${f.max}`,
              size: 'sm'
            };
            return f.multiline ? <Textarea key={f.key} {...common} minRows={2} maxRows={8} /> : <Input key={f.key} {...common} />;
          })}
          {canWrite && (
            <div className="flex flex-wrap gap-2">
              <Button color="primary" onPress={save} isLoading={busy === 'save'} isDisabled={!!busy || !dirty}>Guardar {PRESALE_EMAIL_LANG_NAMES[lang]}</Button>
              <Button variant="flat" onPress={sendTest} isLoading={busy === 'test'} isDisabled={!!busy}>Enviarme una prueba</Button>
              <Button variant="light" onPress={() => { setDrafts((all) => ({ ...all, [lang]: { ...defaults[lang] } })); setNotice(null); }} isDisabled={!!busy}>
                Volver a los textos por defecto
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="rounded-lg border border-default-200 p-3 text-sm">
            <div><span className="text-default-500">Asunto: </span><span className="font-medium">{preview.subject}</span></div>
            {draft.preheader && <div className="text-default-500">Vista previa en la bandeja: {draft.preheader}</div>}
          </div>
          <iframe
            title="Vista previa del correo"
            srcDoc={preview.html}
            sandbox=""
            className="h-[640px] w-full rounded-lg border border-default-200 bg-[#0a0d14]"
          />
        </div>
      </div>
    </div>
  );
}
